import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';

function UptimeBadge({ pct }) {
  const color = pct >= 99
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : pct >= 95
    ? 'bg-amber-100 text-amber-700 border border-amber-200'
    : 'bg-red-100 text-red-700 border border-red-200';
  const dot = pct >= 99 ? 'bg-emerald-500' : pct >= 95 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {pct}%
    </span>
  );
}

export default function BHELDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    client.get('/bhel/dashboard/').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#C1278D] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading dashboard…</p>
      </div>
    </div>
  );

  const totalOpen = data.reduce((s, l) => s + l.open_tickets, 0);
  const avgUptime = data.length ? (data.reduce((s, l) => s + l.uptime_percent, 0) / data.length).toFixed(1) : 0;
  const pmDone = data.reduce((s, l) => s + l.pm_done, 0);
  const pmTotal = data.reduce((s, l) => s + l.pm_total, 0);
  const pmPct = pmTotal > 0 ? Math.round((pmDone / pmTotal) * 100) : 0;
  const uptimeNum = parseFloat(avgUptime);

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold text-[#C1278D] uppercase tracking-widest mb-1">Operations Center</p>
          <h1 className="text-3xl font-bold text-gray-900 leading-tight">BHEL Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            {data[0]?.quarter || 'Q1 FY2627'} &nbsp;·&nbsp; All Locations
          </p>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 bg-white border border-gray-100 rounded-full px-3 py-1.5 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Last updated: just now
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {/* Open Tickets — Amber */}
        <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 shadow-sm p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-amber-100 rounded-full -translate-y-6 translate-x-6 opacity-60" />
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Open Tickets</p>
            <p className="text-4xl font-bold mt-1 text-amber-700">{totalOpen}</p>
            <p className="text-xs text-amber-500 mt-1">Across all locations</p>
          </div>
        </div>

        {/* Avg Uptime — Green or Red */}
        <div className={`relative rounded-2xl border shadow-sm p-5 overflow-hidden ${
          uptimeNum >= 99 ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100'
          : uptimeNum >= 95 ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100'
          : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100'
        }`}>
          <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-6 translate-x-6 opacity-60 ${
            uptimeNum >= 99 ? 'bg-emerald-100' : uptimeNum >= 95 ? 'bg-amber-100' : 'bg-red-100'
          }`} />
          <div className="relative">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 shadow-sm ${
              uptimeNum >= 99 ? 'bg-emerald-500' : uptimeNum >= 95 ? 'bg-amber-500' : 'bg-red-500'
            }`}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className={`text-xs font-semibold uppercase tracking-wider ${
              uptimeNum >= 99 ? 'text-emerald-600' : uptimeNum >= 95 ? 'text-amber-600' : 'text-red-600'
            }`}>Avg Uptime</p>
            <p className={`text-4xl font-bold mt-1 ${
              uptimeNum >= 99 ? 'text-emerald-700' : uptimeNum >= 95 ? 'text-amber-700' : 'text-red-700'
            }`}>{avgUptime}%</p>
            <p className={`text-xs mt-1 ${
              uptimeNum >= 99 ? 'text-emerald-500' : uptimeNum >= 95 ? 'text-amber-500' : 'text-red-500'
            }`}>{uptimeNum >= 99 ? 'Excellent performance' : uptimeNum >= 95 ? 'Moderate — check alerts' : 'Needs immediate attention'}</p>
          </div>
        </div>

        {/* PM Completed — Purple */}
        <div className="relative bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl border border-purple-100 shadow-sm p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-100 rounded-full -translate-y-6 translate-x-6 opacity-60" />
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">PM Completed</p>
            <p className="text-4xl font-bold mt-1 text-[#3D3562]">{pmDone}<span className="text-xl text-purple-300">/{pmTotal}</span></p>
            <div className="mt-2">
              <div className="flex justify-between text-xs text-purple-400 mb-1">
                <span>Progress</span>
                <span>{pmPct}%</span>
              </div>
              <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#3D3562] to-[#C1278D] rounded-full transition-all" style={{ width: `${pmPct}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Active Locations — Blue */}
        <div className="relative bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl border border-sky-100 shadow-sm p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-sky-100 rounded-full -translate-y-6 translate-x-6 opacity-60" />
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-xs font-semibold text-sky-600 uppercase tracking-wider">Active Locations</p>
            <p className="text-4xl font-bold mt-1 text-sky-700">{data.length}</p>
            <p className="text-xs text-sky-500 mt-1">BHEL facilities monitored</p>
          </div>
        </div>
      </div>

      {/* Location Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-[#3D3562] to-[#272244] flex items-center justify-between">
          <div>
            <h2 className="font-bold text-white text-base">Location-wise Status</h2>
            <p className="text-xs text-purple-200 mt-0.5">{data.length} locations · Click any row to view details</p>
          </div>
          <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <th className="px-5 py-3 text-left">Location</th>
              <th className="px-5 py-3 text-center">All Tickets</th>
              <th className="px-5 py-3 text-center">Open</th>
              <th className="px-5 py-3 text-center">Downtime</th>
              <th className="px-5 py-3 text-center">Uptime</th>
              <th className="px-5 py-3 text-center">PM Done</th>
              <th className="px-5 py-3 text-center">RE Attendance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={row.location.id}
                className={`cursor-pointer transition-all duration-150 hover:bg-purple-50 hover:shadow-inner group ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                onClick={() => navigate(`/bhel/location/${row.location.id}`)}
              >
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{row.location.name.charAt(0)}</span>
                    </div>
                    <span className="font-semibold text-gray-900 group-hover:text-[#3D3562] transition-colors">{row.location.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-center text-gray-500 font-medium">{row.total_tickets}</td>
                <td className="px-5 py-3.5 text-center">
                  {row.open_tickets > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {row.open_tickets}
                    </span>
                  ) : (
                    <span className="text-gray-300 font-semibold">0</span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-center">
                  {row.downtime_tickets > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      {row.downtime_tickets}
                    </span>
                  ) : (
                    <span className="text-gray-300 font-semibold">0</span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-center"><UptimeBadge pct={row.uptime_percent} /></td>
                <td className="px-5 py-3.5 text-center">
                  <span className={`font-bold text-sm ${row.pm_done === row.pm_total && row.pm_total > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {row.pm_done}/{row.pm_total}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-center">
                  {row.attendance_percent != null ? (
                    <span className="text-gray-600 font-semibold">{row.attendance_percent}%</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
